import { useFishingDiscipline, disciplineLabel } from "../types/fishingDiscipline";

import React, { useState, useEffect } from "react";
import { DataGrid, DataGridColumn, DataGridAction } from "../components/DataGrid";
import { Campionato } from "../types/campionati/campionato";
import { CampionatiService } from "../services/campionatiService";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (campionato: Omit<Campionato, "id">, id?: number) => Promise<void>;
  initialData?: Partial<Campionato>;
  defaultDiscipline: number;
  error: string | null;
}

const CampionatoModal: React.FC<ModalProps> = ({ open, onClose, onSave, initialData, defaultDiscipline, error }) => {
  const [saving, setSaving] = useState(false);
  const [selectedDiscipline, setSelectedDiscipline] = useState(initialData?.discipline || defaultDiscipline);
  const [nome, setNome] = useState(initialData?.name || "");
  const [anno, setAnno] = useState<string>(initialData?.year || new Date().getFullYear().toString());
  const [descrizione, setDescrizione] = useState(initialData?.description || "");

  React.useEffect(() => {
  setSelectedDiscipline(initialData?.discipline || defaultDiscipline);
  setNome(initialData?.name || "");
  setAnno(initialData?.year || new Date().getFullYear().toString());
  setDescrizione(initialData?.description || "");
  }, [initialData, open, defaultDiscipline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave({ name: nome, year: anno, description: descrizione || undefined, discipline: selectedDiscipline }, initialData?.id); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <form className="bg-white rounded-lg border border-brand-200 p-6 w-full max-w-md " onSubmit={handleSubmit}>
        <h2 className="text-xl font-bold mb-4">{initialData?.id ? "Modifica Campionato" : "Aggiungi Campionato"}</h2>
        {error && <p role="alert" className="text-red-700 mb-4">{error}</p>}
        <label className="block mb-4 font-medium">Disciplina
          <select className="block border rounded px-3 py-2 w-full mt-1" value={selectedDiscipline} onChange={e => setSelectedDiscipline(Number(e.target.value))}>
            <option value={1}>Trout Area</option><option value={2}>Carpe e Carassi</option>
          </select>
        </label>
        <div className="mb-4">
          <label className="block mb-1 font-medium">Nome</label>
          <input className="border rounded px-3 py-2 w-full" value={nome} onChange={e => setNome(e.target.value)} required />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-medium">Anno</label>
          <input type="text" className="border rounded px-3 py-2 w-full" value={anno} onChange={e => setAnno(e.target.value)} required />
        </div>
        <div className="mb-4">
          <label className="block mb-1 font-medium">Descrizione <span className="text-gray-400 text-xs">(opzionale)</span></label>
          <textarea className="border rounded px-3 py-2 w-full" value={descrizione} onChange={e => setDescrizione(e.target.value)} rows={3} />
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={onClose}>Annulla</button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-brand-600 text-white rounded disabled:opacity-50">{saving ? "Salvataggio…" : "Salva"}</button>
        </div>
      </form>
    </div>
  );
};


const CampionatoPage: React.FC = () => {
  const discipline = useFishingDiscipline();
  const [campionati, setCampionati] = useState<{ results: Campionato[]; totalPages: number }>({ results: [], totalPages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<Campionato | null>(null);
  // Debounce searchTerm
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);
  useEffect(() => {
    const loadCampionati = async () => {
      try {
        setLoading(true);
        const data = await CampionatiService.getCampionatiList(
          page,
          pageSize,
          debouncedSearchTerm,
          discipline
        );
        // Adatta la risposta: se paginata, usa results e totalPages
        if (data && Array.isArray(data.results)) {
          setCampionati({ results: data.results, totalPages: data.totalPages || 1 });
        } else if (Array.isArray(data)) {
          setCampionati({ results: data, totalPages: 1 });
        } else {
          setCampionati({ results: [], totalPages: 1 });
        }
        setError(null);
      } catch (err) {
        setError("Errore nel caricamento dei campionati");
      } finally {
        setLoading(false);
      }
    };
    loadCampionati();
  }, [page, pageSize, debouncedSearchTerm, discipline]);

  const handleAdd = () => {
    setError(null);
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (campionato: Campionato) => {
    setError(null);
    setEditData(campionato);
    setModalOpen(true);
  };

  const refreshCampionati = async () => {
    setLoading(true);
    try {
      const data = await CampionatiService.getCampionatiList(page, pageSize, debouncedSearchTerm, discipline);
      if (data && Array.isArray(data.results)) {
        setCampionati({ results: data.results, totalPages: data.totalPages || 1 });
      } else if (Array.isArray(data)) {
        setCampionati({ results: data, totalPages: 1 });
      } else {
        setCampionati({ results: [], totalPages: 1 });
      }
      setError(null);
    } catch (err) {
      setError("Errore nel caricamento dei campionati");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (campionato: Campionato) => {
    setLoading(true);
    try {
      await CampionatiService.deleteCampionato(campionato.id);
      await refreshCampionati();
    } catch {
      setError("Errore nella cancellazione");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data: Omit<Campionato, "id">, id?: number) => {
    setLoading(true);
    try {
      if (id) {
        await CampionatiService.updateCampionato(id, { ...data, discipline: data.discipline || discipline });
      } else {
        await CampionatiService.createCampionato(data);
      }
      setModalOpen(false);
      await refreshCampionati();
    } catch (e: any) {
      setError(e.message || "Errore nel salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const columns: DataGridColumn<Campionato>[] = [
  { key: "name", label: "Nome" },
  { key: "year", label: "Anno" },
  { key: "description", label: "Descrizione", render: (value) => value ? value : <span className="text-gray-400">—</span> },
  ];

  const actions: DataGridAction<Campionato>[] = [
    { label: "Modifica", onClick: handleEdit, variant: "primary" },
    { label: "Elimina", onClick: handleDelete, variant: "danger" },
  ];

  return (
    <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Campionati · {disciplineLabel(discipline)}</h1>
        <button 
          onClick={handleAdd}
          className="bg-brand-700 hover:bg-brand-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
        >
          Nuovo
        </button>
      </div>
      {/* <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Campionati · {disciplineLabel(discipline)}</h1>
        <button className="bg-brand-600 text-white px-4 py-2 rounded" onClick={handleAdd}>Aggiungi Campionato</button>
      </div> */}
      {error && <div className="text-red-600 mb-2">{error}</div>}
      <DataGrid
        data={campionati.results}
        columns={columns}
        actions={actions}
        loading={loading}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        pageSize={pageSize}
        currentPage={page}
        totalPages={campionati.totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyStateTitle="Nessun campionato"
        emptyStateDescription="Aggiungi un nuovo campionato per iniziare."
      />
      <CampionatoModal
        error={error}
        defaultDiscipline={discipline}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initialData={editData ?? undefined}
      />
    </div>
  );
};

export default CampionatoPage;
